import i18next, { type BackendModule, type InitOptions, type ReadCallback, type ResourceKey, type Services } from 'i18next'

export type DynamicResource = Record<string, Record<string, () => Promise<{ default: ResourceKey }>>>

const dynamicResourceLoadListeners: Set<(language: string, namespace: string, entries: ResourceKey) => void> = new Set()

export function notifyDynamicResourceLoad(language: string, namespace: string, entries: unknown): void {
  // console.debug(`Notify dynamic resource load for language: ${language}, namespace: ${namespace}`, entries)
  const data = entries as { default: ResourceKey }
  for (const listener of dynamicResourceLoadListeners) {
    listener(language, namespace, data.default)
  }
}

dynamicResourceLoadListeners.add((language, namespace, entries) => {
  // console.debug(`Dynamic resource loaded for language: ${language}, namespace: ${namespace}`, entries)
  i18next.addResourceBundle(language, namespace, entries, true, true)
})

export interface LazyResourceBackendOptions {
  resources: DynamicResource
}

export class LazyResourceBackend implements BackendModule {
  readonly name = 'LazyResourceBackend'
  readonly type = 'backend' as const

  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: no clue why it stayed
  private _options: LazyResourceBackendOptions = {
    resources: {},
  }

  init(_services: Services, backendOptions: object, _i18nextOptions: InitOptions): void {
    this._options = backendOptions as LazyResourceBackendOptions
  }

  read(language: string, namespace: string, callback: ReadCallback): void {
    const languageResources = this._options.resources[language]
    if (!languageResources) {
      callback(new Error(`No resources found for language: ${language}`), false)
      return
    }
    const resourceLoader = languageResources[namespace]
    if (!resourceLoader) {
      callback(new Error(`No resources found for namespace: ${namespace} in language: ${language}`), false)
      return
    }
    resourceLoader()
      .then((resources) => {
        // console.debug(`Loaded resources for language: ${language}, namespace: ${namespace}`, resources.default)
        callback(null, resources.default)
      })
      .catch((error) => {
        console.error(`Error loading resources for language: ${language}, namespace: ${namespace}`, error)
        callback(error, false)
      })
  }
}
